import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Clock, ArrowRight, Instagram, Facebook } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// TikTok icon component (not available in lucide-react)
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
  </svg>
);

const Footer = () => {
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (email) {
      try {
        await axios.post(`${API}/subscribe`, { email });
        setSubscribed(true);
        setEmail('');
      } catch (error) {
        console.error('Subscribe error:', error);
      }
    }
  };

  return (
    <footer className="bg-[#1A1A1A]" data-testid="footer">
      {/* Newsletter Section */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[#D4AF37] uppercase tracking-[0.2em] text-xs font-medium mb-3">Newsletter</p>
              <h3 
                className="text-2xl md:text-3xl font-bold text-white mb-2"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Stay Connected
              </h3>
              <p className="text-gray-400 text-sm">
                Subscribe for exclusive offers, new arrivals, and cultural fashion inspiration.
              </p>
            </div>
            <div>
              {subscribed ? (
                <p className="text-[#D4AF37] font-medium">Thank you for subscribing!</p>
              ) : (
                <form onSubmit={handleSubscribe} className="flex">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="flex-1 px-5 py-4 bg-white/5 border border-white/20 text-white text-sm placeholder:text-gray-500 focus:outline-none focus:border-[#D4AF37] transition-colors"
                    required
                  />
                  <button
                    type="submit"
                    className="px-6 bg-[#D4AF37] text-white hover:bg-[#B5952F] transition-colors flex items-center gap-2"
                  >
                    <span className="hidden sm:inline text-sm uppercase tracking-widest font-bold">Subscribe</span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 lg:gap-12">
          {/* Brand */}
          <div className="col-span-2 md:col-span-1">
            <Link to="/">
              <img 
                src="https://customer-assets.emergentagent.com/job_nepora-shop/artifacts/pnjbs1wu_ChatGPT%20Image%20Feb%2026%2C%202026%2C%2012_48_27%20PM.png" 
                alt="Nepora" 
                className="h-24 w-auto mb-6"
              />
            </Link>
            <p className="text-sm text-gray-400 leading-relaxed mb-6">
              Your destination for authentic Nepali fashion, jewelry, and cultural treasures.
            </p>
            <div className="flex gap-4">
              <a 
                href="https://www.instagram.com/neporaclothingaccessories?igsh=em1raHdiOWF4c2V4&utm_source=qr" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 border border-white/20 flex items-center justify-center text-gray-400 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
                data-testid="social-instagram"
                aria-label="Follow us on Instagram"
              >
                <Instagram className="w-4 h-4" />
              </a>
              <a 
                href="https://www.facebook.com/share/18KTsVygnP/?mibextid=wwXIfr" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 border border-white/20 flex items-center justify-center text-gray-400 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
                data-testid="social-facebook"
                aria-label="Follow us on Facebook"
              >
                <Facebook className="w-4 h-4" />
              </a>
              <a 
                href="https://www.tiktok.com/@neporaforyou?_r=1&_t=ZP-94ag5KPzQ5P" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 border border-white/20 flex items-center justify-center text-gray-400 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
                data-testid="social-tiktok"
                aria-label="Follow us on TikTok"
              >
                <TikTokIcon className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Shop */}
          <div>
            <h4 className="text-white text-xs uppercase tracking-widest font-bold mb-6">Shop</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/shop" className="text-sm text-gray-400 hover:text-[#D4AF37] transition-colors" data-testid="footer-shop-all">
                  Shop All
                </Link>
              </li>
              <li>
                <Link to="/category/deals" className="text-sm text-gray-400 hover:text-[#D4AF37] transition-colors" data-testid="footer-deals">
                  Under $10
                </Link>
              </li>
              <li>
                <Link to="/category/sarees" className="text-sm text-gray-400 hover:text-[#D4AF37] transition-colors" data-testid="footer-sarees">
                  Sarees
                </Link>
              </li>
              <li>
                <Link to="/category/jewelry" className="text-sm text-gray-400 hover:text-[#D4AF37] transition-colors" data-testid="footer-jewelry">
                  Jewelry
                </Link>
              </li>
              <li>
                <Link to="/category/clothing" className="text-sm text-gray-400 hover:text-[#D4AF37] transition-colors" data-testid="footer-clothing">
                  Clothing
                </Link>
              </li>
            </ul>
          </div>

          {/* Help */}
          <div>
            <h4 className="text-white text-xs uppercase tracking-widest font-bold mb-6">Help</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/about" className="text-sm text-gray-400 hover:text-[#D4AF37] transition-colors" data-testid="footer-about">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-sm text-gray-400 hover:text-[#D4AF37] transition-colors" data-testid="footer-contact">
                  Contact
                </Link>
              </li>
              <li>
                <Link to="/track-order" className="text-sm text-gray-400 hover:text-[#D4AF37] transition-colors" data-testid="footer-track-order">
                  Track My Order
                </Link>
              </li>
              <li>
                <Link to="/faq" className="text-sm text-gray-400 hover:text-[#D4AF37] transition-colors" data-testid="footer-faq">
                  FAQ
                </Link>
              </li>
              <li>
                <Link to="/policies" className="text-sm text-gray-400 hover:text-[#D4AF37] transition-colors" data-testid="footer-policies">
                  Shipping & Returns
                </Link>
              </li>
            </ul>
          </div>

          {/* Store */}
          <div>
            <h4 className="text-white text-xs uppercase tracking-widest font-bold mb-6">Visit Us</h4>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-[#D4AF37] mt-1 flex-shrink-0" />
                <div className="text-sm text-gray-400">
                  <p className="text-white font-medium">Nepora Store</p>
                  <p>6575 E Livingston Ave</p>
                  <p>Reynoldsburg, OH 43068</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Clock className="w-4 h-4 text-[#D4AF37] mt-1 flex-shrink-0" />
                <div className="text-sm text-gray-400">
                  <p className="text-white font-medium">Open Daily</p>
                  <p>9:00 AM – 9:00 PM</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <svg className="w-4 h-4 text-[#D4AF37] mt-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                <div className="text-sm text-gray-400">
                  <a href="tel:614-762-2066" className="hover:text-[#D4AF37] transition-colors">614-762-2066</a>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <svg className="w-4 h-4 text-[#D4AF37] mt-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <div className="text-sm text-gray-400">
                  <a href="mailto:info@neporaforyou.com" className="hover:text-[#D4AF37] transition-colors">info@neporaforyou.com</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-xs text-gray-500">
              © {new Date().getFullYear()} Nepora Clothing & Accessories. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <Link to="/policies" className="text-xs text-gray-500 hover:text-gray-400 transition-colors">
                Privacy Policy
              </Link>
              <Link to="/policies" className="text-xs text-gray-500 hover:text-gray-400 transition-colors">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
